import { useState } from 'react';
import { RECAPTCHA_SITE_KEY } from '@/config';
import { BUSINESS } from '../../../site';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

let recaptchaLoad: Promise<void> | null = null;

// reCAPTCHA is ~385 KB and blocks the main thread, so it loads when someone starts using the form
// rather than on every page view.
function loadRecaptcha(): Promise<void> {
  if (!recaptchaLoad) {
    recaptchaLoad = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        recaptchaLoad = null;
        reject(new Error('reCAPTCHA failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return recaptchaLoad;
}

async function getRecaptchaToken(): Promise<string> {
  await loadRecaptcha();
  return new Promise((resolve, reject) => {
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) {
      reject(new Error('reCAPTCHA not loaded'));
      return;
    }
    grecaptcha.ready(() => {
      grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'quote_request' }).then(resolve, reject);
    });
  });
}

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  // Honeypot: hidden from people, so only bots fill it in
  const [website, setWebsite] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setIsSuccess(false);
    setIsError(false);
    try {
      const recaptchaToken = await getRecaptchaToken();
      const res = await fetch('/api/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, website, recaptchaToken }),
      });
      if (!res.ok) throw new Error(`Quote request failed: ${res.status}`);
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch {
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-28 px-6 bg-gradient-to-b from-white to-[#f7f8fa]">
      <div className="max-w-7xl mx-auto grid gap-12 items-start grid-cols-1 min-[1100px]:grid-cols-[1fr_1fr]">
        {/* Left: Form */}
        <div className="bg-white rounded-[28px] shadow-[0_1px_2px_rgba(16,24,40,0.04),0_24px_48px_-24px_rgba(16,24,40,0.12)] border border-[#edeef0] p-12">
          <p className="text-[13px] font-bold tracking-[0.09em] uppercase text-[#0b5b96] mb-[14px]">Get Your Free Quote</p>
          <h2 className="font-extrabold text-[#161a20] mb-[34px]" style={{ fontSize: 'clamp(1.9rem,3.4vw,2.4rem)' }}>Let's Get Started</h2>

          <form onSubmit={handleSubmit} onFocus={() => loadRecaptcha().catch(() => {})} id="contact-form">
            <div aria-hidden="true" className="absolute -left-[10000px] w-px h-px overflow-hidden">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-[22px]">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#3a4150] mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Smith"
                  className="w-full px-4 py-[13px] border-[1.5px] border-[#dde1e7] rounded-xl text-[15.5px] outline-none transition-colors duration-200 focus:border-[#0b5b96]"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#3a4150] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  className="w-full px-4 py-[13px] border-[1.5px] border-[#dde1e7] rounded-xl text-[15.5px] outline-none transition-colors duration-200 focus:border-[#0b5b96]"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-[#3a4150] mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="(540) 786-2834"
                  className="w-full px-4 py-[13px] border-[1.5px] border-[#dde1e7] rounded-xl text-[15.5px] outline-none transition-colors duration-200 focus:border-[#0b5b96]"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-[#3a4150] mb-2">
                  Service Type *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-[13px] border-[1.5px] border-[#dde1e7] rounded-xl text-[15.5px] outline-none cursor-pointer bg-white"
                >
                  <option value="">Select a service</option>
                  <option value="Office Space Cleaning">Office Space Cleaning</option>
                  <option value="After-Hours Cleaning">After-Hours Cleaning</option>
                  <option value="Recurring Service">Recurring Service</option>
                  <option value="Deep Cleaning">Deep Cleaning</option>
                  <option value="Multi-Location Service">Multi-Location Service</option>
                  <option value="Specialty Cleaning">Specialty Cleaning</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#3a4150] mb-2">
                  Additional Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  placeholder="Tell us about your cleaning needs..."
                  className="w-full px-4 py-[13px] border-[1.5px] border-[#dde1e7] rounded-xl text-[15.5px] outline-none resize-none transition-colors duration-200 focus:border-[#0b5b96]"
                ></textarea>
                <p className="text-[12.5px] text-[#5b6472] mt-1.5">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0b5b96] hover:bg-[#084876] text-white py-[17px] rounded-2xl font-bold text-[16.5px] shadow-[0_12px_26px_-10px_rgba(11,91,150,0.5)] flex items-center justify-center gap-[10px] transition-all duration-200 cursor-pointer disabled:opacity-60"
              >
                {isSubmitting ? 'Sending...' : 'Get Free Quote'}
                <i className={isSubmitting ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'}></i>
              </button>

              {isSuccess && (
                <div className="bg-[#eef9ef] border border-[#cdeccd] text-[#1e6b2e] px-[18px] py-4 rounded-2xl flex gap-3 items-start">
                  <i className="ri-checkbox-circle-fill text-xl mt-0.5"></i>
                  <div>
                    <p className="font-bold mb-0.5 text-[15px]">Thank you for your request!</p>
                    <p className="text-sm">We'll respond within 24 hours with your free quote.</p>
                  </div>
                </div>
              )}

              {isError && (
                <div className="bg-[#fdeeee] border border-[#f3caca] text-[#8a1f1f] px-[18px] py-4 rounded-2xl flex gap-3 items-start">
                  <i className="ri-error-warning-fill text-xl mt-0.5"></i>
                  <div>
                    <p className="font-bold mb-0.5 text-[15px]">Something went wrong.</p>
                    <p className="text-sm">
                      Please call <a href={BUSINESS.phoneHref} className="underline">{BUSINESS.phoneDisplay}</a> or email{' '}
                      <a href={`mailto:${BUSINESS.email}`} className="underline">{BUSINESS.email}</a>.
                    </p>
                  </div>
                </div>
              )}

              <p className="text-center text-xs text-[#5b6472] mt-1">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a> apply.
              </p>
            </div>
          </form>
        </div>

        {/* Right: Contact Info */}
        <div className="rounded-[28px] shadow-[0_24px_48px_-24px_rgba(11,91,150,0.4)] p-12 text-white h-full" style={{ background: 'linear-gradient(135deg,#0b5b96,#083f68)' }}>
          <h2 className="text-[26px] font-extrabold mb-[34px]">Contact Us</h2>

          <div className="flex flex-col gap-[30px]">
            <div className="flex gap-4 items-start">
              <div className="w-12 h-12 rounded-full bg-white/[.16] flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-fill text-[19px]"></i>
              </div>
              <div>
                <p className="text-[13.5px] text-white/75 mb-1">Call or Text</p>
                <a href={BUSINESS.phoneHref} className="text-[22px] font-bold text-white hover:text-[#5FA42B]">{BUSINESS.phoneDisplay}</a>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-12 h-12 rounded-full bg-white/[.16] flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-fill text-[19px]"></i>
              </div>
              <div>
                <p className="text-[13.5px] text-white/75 mb-1">Email Us</p>
                <a href={`mailto:${BUSINESS.email}`} className="text-[16.5px] text-white hover:text-[#5FA42B]">{BUSINESS.email}</a>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-12 h-12 rounded-full bg-white/[.16] flex items-center justify-center flex-shrink-0">
                <i className="ri-time-fill text-[19px]"></i>
              </div>
              <div>
                <p className="text-[13.5px] text-white/75 mb-1">Business Hours</p>
                <p className="text-[16.5px] leading-relaxed">{BUSINESS.hours.label}<br />{BUSINESS.hours.time}</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-12 h-12 rounded-full bg-white/[.16] flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-fill text-[19px]"></i>
              </div>
              <div>
                <p className="text-[13.5px] text-white/75 mb-1">Service Area</p>
                <p className="text-[16.5px] leading-relaxed">Fredericksburg, VA &amp;<br />Northern Virginia</p>
              </div>
            </div>
          </div>

          {/* Follow Us hidden until social links exist (all hrefs are "#")
          <div className="mt-11 pt-8 border-t border-white/[.18]">
            <p className="font-bold text-base mb-4">Follow Us</p>
            <div className="flex gap-3">
              <a href="#" aria-label="Facebook" className="w-[46px] h-[46px] rounded-full bg-white/[.16] hover:bg-white/30 flex items-center justify-center text-white">
                <i className="ri-facebook-fill text-[19px]"></i>
              </a>
              <a href="#" aria-label="Instagram" className="w-[46px] h-[46px] rounded-full bg-white/[.16] hover:bg-white/30 flex items-center justify-center text-white">
                <i className="ri-instagram-fill text-[19px]"></i>
              </a>
              <a href="#" aria-label="Google" className="w-[46px] h-[46px] rounded-full bg-white/[.16] hover:bg-white/30 flex items-center justify-center text-white">
                <i className="ri-google-fill text-[19px]"></i>
              </a>
            </div>
          </div>
          */}
        </div>
      </div>
    </section>
  );
}
